import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { formatMoney } from '@theme/money-formatting';
import { syncHeaderCartCount } from '@theme/gullyo-header-cart-count';
import { applyCartSectionsUpdate, awaitViewTransitionReady } from '@theme/gullyo-cart-sync';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {object} LineUpdateState
 * @property {number} targetQuantity
 * @property {number} requestId
 * @property {number} sentQuantity
 * @property {boolean} inFlight
 * @property {LineSnapshot | null} snapshot
 * @property {CartSnapshot | null} cartSnapshot
 */

/**
 * @typedef {object} LineSnapshot
 * @property {string} quantityValue
 * @property {string} quantityDefault
 * @property {string | null} removeLabel
 * @property {string} linePriceCents
 * @property {string} quantity
 */

/**
 * @typedef {object} CartSnapshot
 * @property {number} itemCount
 * @property {number} totalCents
 * @property {string | null | undefined} totalText
 * @property {string | null | undefined} totalValue
 * @property {string | null | undefined} headingCountText
 * @property {Node[] | null} componentNodes
 * @property {boolean} wasEmptyOptimistic
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /** @type {Map<number, LineUpdateState>} */
  #lineUpdates = new Map();

  /** @type {Map<number, ReturnType<typeof setTimeout>>} */
  #quantitySyncTimers = new Map();

  /** @type {number | null} */
  #pendingFocusLine = null;

  /** @type {boolean} */
  #pendingFocusEmptyCart = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #errorToastTimer = null;

  static #QUANTITY_SYNC_DEBOUNCE_MS = 120;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);

    for (const timer of this.#quantitySyncTimers.values()) {
      clearTimeout(timer);
    }
    this.#quantitySyncTimers.clear();

    if (this.#errorToastTimer) {
      clearTimeout(this.#errorToastTimer);
    }
  }

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#onQuantityChange(event);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const sourceElement = event.target instanceof Element ? event.target : undefined;

    this.updateCartLine({
      line,
      quantity,
      sourceElement,
      action: quantity === 0 ? 'clear' : 'change',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The originating click event.
   */
  onLineItemRemove(line, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const sourceElement = event?.target instanceof Element ? event.target : undefined;

    this.removeCartLineOptimistically(line, sourceElement);
  }

  /**
   * Shared optimistic removal — used by REMOVE and minus-at-quantity-1.
   * @param {number} line - 1-based cart line index.
   * @param {Element} [sourceElement] - Control that initiated the update.
   */
  removeCartLineOptimistically(line, sourceElement) {
    this.updateCartLine({
      line,
      quantity: 0,
      sourceElement,
      action: 'clear',
    });
  }

  /**
   * Shared cart line update — optimistic UI, one Shopify request per resolved action.
   * @param {object} config
   * @param {number} config.line - 1-based cart line index.
   * @param {number} config.quantity - Target quantity (0 removes).
   * @param {Element} [config.sourceElement] - Control that initiated the update.
   * @param {'change' | 'clear'} [config.action='change']
   */
  updateCartLine({ line, quantity, sourceElement, action = 'change' }) {
    if (!line) return;

    if (quantity === 0) {
      const rowCount = this.refs.cartItemRows?.length ?? 0;
      this.#pendingFocusEmptyCart = rowCount <= 1;
      if (!this.#pendingFocusEmptyCart) {
        this.#pendingFocusLine = line;
      }
    } else {
      this.#clearQuantitySyncTimer(line);
    }

    const state = this.#getLineState(line);

    if (state.inFlight && state.targetQuantity === 0 && quantity === 0) {
      return;
    }

    const previousQuantity = this.#getLineQuantity(line);
    state.targetQuantity = quantity;

    if (quantity === 0) {
      this.#removeCartLineOptimistically(line, sourceElement, state, previousQuantity);
      if (!state.inFlight) {
        this.#flushLineUpdate(line, action);
      }
      return;
    }

    this.#updateCartLineOptimistically(line, quantity, state, previousQuantity);

    if (state.inFlight) {
      return;
    }

    this.#scheduleQuantitySync(line, action);
  }

  /**
   * @param {number} line
   * @param {'change' | 'clear'} action
   */
  #scheduleQuantitySync(line, action) {
    this.#clearQuantitySyncTimer(line);

    const timer = window.setTimeout(() => {
      this.#quantitySyncTimers.delete(line);
      this.#flushLineUpdate(line, action);
    }, CartItemsComponent.#QUANTITY_SYNC_DEBOUNCE_MS);

    this.#quantitySyncTimers.set(line, timer);
  }

  /**
   * @param {number} line
   */
  #clearQuantitySyncTimer(line) {
    const timer = this.#quantitySyncTimers.get(line);
    if (timer) {
      clearTimeout(timer);
      this.#quantitySyncTimers.delete(line);
    }
  }

  /**
   * @param {number} line
   * @param {'change' | 'clear'} action
   */
  #flushLineUpdate(line, action) {
    const state = this.#getLineState(line);
    if (state.inFlight) return;

    const quantity = state.targetQuantity;
    const requestId = ++state.requestId;
    state.sentQuantity = quantity;
    state.inFlight = true;

    this.#setLineBusy(line, true, quantity === 0 ? 'removing' : 'updating');

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const scrollContainer = this.#getScrollContainer();
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const drawerWasOpen = Boolean(this.closest('cart-drawer-component')?.querySelector('dialog[open]'));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const row = this.refs.cartItemRows?.[line - 1];
    const lineKey = row?.dataset.key;
    const body = JSON.stringify({
      ...(lineKey ? { id: lineKey } : { line }),
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then(async (responseText) => {
        if (requestId !== state.requestId) return;

        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#pendingFocusLine = null;
          this.#pendingFocusEmptyCart = false;
          this.#rollbackLineUpdate(line, state, parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        state.cartSnapshot = null;
        state.snapshot = null;

        this.#updateQuantitySelectors(parsedResponseText);

        syncHeaderCartCount(newCartItemCount);

        await applyCartSectionsUpdate(parsedResponseText.sections, { fallbackCount: newCartItemCount });
        this.#updateQuantitySelectors(parsedResponseText);

        document.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            sectionsHandled: true,
          })
        );

        requestAnimationFrame(() => {
          if (scrollContainer) {
            scrollContainer.scrollTop = Math.min(scrollTop, scrollContainer.scrollHeight);
          }
        });

        this.#syncDrawerEmptyState();
        this.#restoreDrawerOpenState(drawerWasOpen);
        this.#applyPostUpdateFocus();
        this.#updateCartQuantitySelectorButtonStates();
        this.#lineUpdates.delete(line);
      })
      .catch((error) => {
        if (requestId !== state.requestId) return;

        console.error('[cart-items] Cart update failed:', error);
        this.#pendingFocusLine = null;
        this.#pendingFocusEmptyCart = false;
        this.#rollbackLineUpdate(line, state);
      })
      .finally(() => {
        if (requestId !== state.requestId) return;

        state.inFlight = false;
        this.#setLineBusy(line, false);
        const escapedSectionId = window.CSS?.escape ? window.CSS.escape(this.sectionId) : this.sectionId;
        const currentComponent = document.querySelector(
          `cart-items-component[data-section-id="${escapedSectionId}"]`
        );
        if (currentComponent instanceof CartItemsComponent) {
          currentComponent.releaseTransientControls(line);
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);

        if (state.targetQuantity !== state.sentQuantity) {
          this.#flushLineUpdate(line, state.targetQuantity === 0 ? 'clear' : 'change');
        }
      });
  }

  /**
   * @param {number} line
   * @param {Element | undefined} sourceElement
   * @param {LineUpdateState} state
   * @param {number} previousQuantity
   */
  #removeCartLineOptimistically(line, sourceElement, state, previousQuantity) {
    if (!state.snapshot) {
      state.snapshot = this.#captureLineSnapshot(line);
    }

    this.#ensureCartSnapshot(state);
    this.#applyOptimisticRemoveVisual(line, sourceElement);
    this.#applyOptimisticCartDelta(line, previousQuantity, 0, state);
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @param {LineUpdateState} state
   * @param {number} previousQuantity
   */
  #updateCartLineOptimistically(line, quantity, state, previousQuantity) {
    if (!state.snapshot) {
      state.snapshot = this.#captureLineSnapshot(line);
    }

    this.#ensureCartSnapshot(state);
    this.#applyOptimisticQuantity(line, quantity, state);
    this.#applyOptimisticCartDelta(line, previousQuantity, quantity, state);
  }

  /**
   * @param {number} line
   * @returns {LineUpdateState}
   */
  #getLineState(line) {
    let state = this.#lineUpdates.get(line);
    if (!state) {
      state = {
        targetQuantity: 0,
        requestId: 0,
        sentQuantity: 0,
        inFlight: false,
        snapshot: null,
        cartSnapshot: null,
      };
      this.#lineUpdates.set(line, state);
    }
    return state;
  }

  /**
   * @param {LineUpdateState} state
   */
  #ensureCartSnapshot(state) {
    if (state.cartSnapshot) return;

    state.cartSnapshot = {
      itemCount: this.#getCartItemCount(),
      totalCents: this.#getCartTotalCents(),
      totalText: this.refs.cartTotal?.textContent,
      totalValue: this.refs.cartTotal?.getAttribute('value'),
      headingCountText: this.querySelector('.gullyo-cart-title__count')?.textContent,
      componentNodes: null,
      wasEmptyOptimistic: false,
    };
  }

  /**
   * @param {number} line
   * @returns {number}
   */
  #getLineQuantity(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return 0;

    const datasetQuantity = row.dataset.quantity;
    if (datasetQuantity) {
      return parseInt(datasetQuantity, 10) || 0;
    }

    const quantityInput = row.querySelector('input[type="number"]');
    return quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.value, 10) || 0 : 0;
  }

  /**
   * @returns {number}
   */
  #getCartItemCount() {
    const hiddenCount = this.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    if (hiddenCount) {
      return parseInt(hiddenCount, 10) || 0;
    }

    return (this.refs.cartItemRows ?? []).reduce((total, row) => {
      if (!(row instanceof HTMLElement)) return total;
      return total + (parseInt(row.dataset.quantity || '0', 10) || 0);
    }, 0);
  }

  /**
   * @param {number} itemCount
   */
  #setCartItemCount(itemCount) {
    const hiddenCount = this.querySelector('[ref="cartItemCount"]');
    if (hiddenCount) {
      hiddenCount.textContent = String(itemCount);
    }

    const headingCount = this.querySelector('.gullyo-cart-title__count');
    if (headingCount instanceof HTMLElement) {
      if (headingCount.textContent) {
        headingCount.textContent = headingCount.textContent.replace(/\d+/g, String(itemCount));
      }
    }

    syncHeaderCartCount(itemCount);
  }

  /**
   * @returns {number}
   */
  #getCartTotalCents() {
    const { cartTotal } = this.refs;
    const datasetTotal = cartTotal?.dataset.totalCents;
    if (datasetTotal) {
      return parseInt(datasetTotal, 10) || 0;
    }

    return 0;
  }

  /**
   * @param {number} totalCents
   */
  #setCartTotalCents(totalCents) {
    const { cartTotal } = this.refs;
    if (!cartTotal) return;

    const formatted = formatMoney(totalCents, this.#getMoneyFormat(), this.#getCurrency());
    cartTotal.textContent = formatted;
    cartTotal.setAttribute('value', formatted);
    cartTotal.dataset.totalCents = String(totalCents);
  }

  /**
   * @returns {string}
   */
  #getCurrency() {
    const selectedCurrency = document.body?.dataset.gullyoCurrency;
    if (selectedCurrency) return selectedCurrency;
    return this.dataset.currency || window.Shopify?.currency?.active || 'USD';
  }

  /**
   * @returns {string}
   */
  #getMoneyFormat() {
    return this.dataset.moneyFormatCurrency || this.dataset.moneyFormat || '{{amount}}';
  }

  /**
   * @param {number} line
   * @param {number} fromQty
   * @param {number} toQty
   * @param {LineUpdateState} state
   */
  #applyOptimisticCartDelta(line, fromQty, toQty, state) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return;

    const linePriceCents = parseInt(row.dataset.linePriceCents || '0', 10);
    const unitPrice = fromQty > 0 ? Math.round(linePriceCents / fromQty) : 0;

    let totalDelta = 0;
    let nextLinePriceCents = 0;

    if (toQty === 0) {
      totalDelta = -linePriceCents;
    } else {
      nextLinePriceCents = unitPrice * toQty;
      totalDelta = nextLinePriceCents - linePriceCents;
      row.dataset.linePriceCents = String(nextLinePriceCents);
      row.dataset.quantity = String(toQty);
    }

    const nextItemCount = Math.max(0, this.#getCartItemCount() + (toQty - fromQty));
    const nextTotalCents = Math.max(0, this.#getCartTotalCents() + totalDelta);

    this.#setCartItemCount(nextItemCount);
    this.#setCartTotalCents(nextTotalCents);
    this.#dispatchOptimisticCartUpdate(nextItemCount);

    if (nextItemCount === 0 && this.isDrawer) {
      this.#showOptimisticEmptyCart(state);
    }
  }

  /**
   * @param {number} itemCount
   */
  #dispatchOptimisticCartUpdate(itemCount) {
    syncHeaderCartCount(itemCount);

    document.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        optimistic: true,
      })
    );
  }

  /**
   * @param {LineUpdateState} state
   */
  #showOptimisticEmptyCart(state) {
    const template = document.getElementById('empty-cart-template');
    if (!template || !this.isDrawer || !state.cartSnapshot) return;

    if (!state.cartSnapshot.componentNodes) {
      state.cartSnapshot.componentNodes = Array.from(this.childNodes, (node) => node.cloneNode(true));
      state.cartSnapshot.wasEmptyOptimistic = true;
    }

    const fragment = template.content.cloneNode(true);
    const emptyHeading = fragment.querySelector('#cart-drawer-heading-empty-template');
    if (emptyHeading instanceof HTMLElement) emptyHeading.id = 'cart-drawer-heading-empty';
    this.replaceChildren(...fragment.childNodes);

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.classList.add('cart-drawer--empty');
      dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
    }
  }

  /**
   * @param {LineUpdateState} state
   */
  #revertOptimisticCartMetrics(state) {
    const snapshot = state.cartSnapshot;
    if (!snapshot) return;

    if (snapshot.wasEmptyOptimistic && snapshot.componentNodes) {
      this.replaceChildren(...snapshot.componentNodes);

      const drawer = this.closest('cart-drawer-component');
      const dialog = drawer?.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement) {
        dialog.classList.remove('cart-drawer--empty');
        dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
      }
    } else {
      this.#setCartItemCount(snapshot.itemCount);
      this.#setCartTotalCents(snapshot.totalCents);

      const { cartTotal } = this.refs;
      if (cartTotal && snapshot.totalText) {
        cartTotal.textContent = snapshot.totalText;
        if (snapshot.totalValue) {
          cartTotal.setAttribute('value', snapshot.totalValue);
        }
        cartTotal.dataset.totalCents = String(snapshot.totalCents);
      }

      const headingCount = this.querySelector('.gullyo-cart-title__count');
      if (headingCount instanceof HTMLElement && snapshot.headingCountText) {
        headingCount.textContent = snapshot.headingCountText;
      }
    }

    this.#dispatchOptimisticCartUpdate(snapshot.itemCount);
    this.#syncDrawerEmptyState();
    state.cartSnapshot = null;
  }

  /**
   * @param {number} line
   * @returns {LineSnapshot | null}
   */
  #captureLineSnapshot(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return null;

    const quantityInput = row.querySelector('input[type="number"]');
    const removeLabel = row.querySelector('.gullyo-cart-item__remove-label');

    return {
      quantityValue: quantityInput instanceof HTMLInputElement ? quantityInput.value : '',
      quantityDefault: quantityInput instanceof HTMLInputElement ? quantityInput.defaultValue : '',
      removeLabel: removeLabel instanceof HTMLElement ? removeLabel.textContent : null,
      linePriceCents: row.dataset.linePriceCents || '',
      quantity: row.dataset.quantity || '',
    };
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @param {LineUpdateState} state
   */
  #applyOptimisticQuantity(line, quantity, state) {
    const row = this.refs.cartItemRows?.[line - 1];
    const quantityInput = row?.querySelector('input[type="number"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(quantity);
    }

    const selector = row?.querySelector('cart-quantity-selector-component');
    if (selector && typeof /** @type {any} */ (selector).updateButtonStates === 'function') {
      /** @type {any} */ (selector).updateButtonStates();
    }
  }

  /**
   * @param {number} line
   * @param {Element | undefined} sourceElement
   */
  #applyOptimisticRemoveVisual(line, sourceElement) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return;

    row.style.setProperty('--gullyo-cart-item-height', `${row.offsetHeight}px`);
    row.classList.add('gullyo-cart-item--removing', 'gullyo-cart-item--removing-collapse');
    row.setAttribute('aria-hidden', 'true');

    row.querySelectorAll('button, input').forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = true;
      }
    });

    if (sourceElement instanceof HTMLElement) {
      row.dataset.gullyoRemoveSource = 'true';
    }
  }

  /**
   * @param {number} line
   * @param {LineUpdateState} state
   */
  #revertLineOptimistic(line, state) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return;

    row.classList.remove(
      'gullyo-cart-item--removing',
      'gullyo-cart-item--removing-collapse',
      'gullyo-cart-item--updating'
    );
    row.removeAttribute('aria-hidden');
    row.removeAttribute('aria-busy');
    row.style.removeProperty('--gullyo-cart-item-height');
    delete row.dataset.gullyoRemoveSource;

    const snapshot = state.snapshot;
    if (snapshot) {
      const quantityInput = row.querySelector('input[type="number"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = snapshot.quantityValue || snapshot.quantityDefault;
      }

      const removeLabel = row.querySelector('.gullyo-cart-item__remove-label');
      if (removeLabel instanceof HTMLElement && snapshot.removeLabel) {
        removeLabel.textContent = snapshot.removeLabel;
        delete removeLabel.dataset.gullyoOriginalLabel;
      }

      if (snapshot.linePriceCents) {
        row.dataset.linePriceCents = snapshot.linePriceCents;
      }

      if (snapshot.quantity) {
        row.dataset.quantity = snapshot.quantity;
      }
    }

    row.querySelectorAll('button, input').forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = false;
      }
    });

    const selector = row.querySelector('cart-quantity-selector-component');
    if (selector && typeof /** @type {any} */ (selector).updateButtonStates === 'function') {
      /** @type {any} */ (selector).updateButtonStates();
    }

    state.snapshot = null;
  }

  /**
   * @param {number} line
   * @param {LineUpdateState} state
   * @param {string} [serverError]
   */
  #rollbackLineUpdate(line, state, serverError) {
    if (serverError) {
      console.error('[cart-items] Shopify cart error:', serverError);
    }

    this.#revertOptimisticCartMetrics(state);
    this.#revertLineOptimistic(line, state);
    this.#showCartUpdateError();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await awaitViewTransitionReady();
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#syncDrawerEmptyState();
      return;
    }
    if (event.target === this) return;
    if (event.detail?.data?.optimistic) return;

    if (event.detail?.data?.sectionsHandled) {
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml && event.detail.data.sections) {
      if (!event.detail.data.sectionsHandled) {
        event.detail.data.sectionsHandled = true;
        await applyCartSectionsUpdate(event.detail.data.sections);
      }
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    if (cartItemsHtml) {
      await awaitViewTransitionReady();
      await morphSection(this.sectionId, cartItemsHtml);
      this.#syncDrawerEmptyState();
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    await awaitViewTransitionReady();
    await sectionRenderer.renderSection(this.sectionId, { cache: false });
    this.#syncDrawerEmptyState();
    this.#updateCartQuantitySelectorButtonStates();
  };

  #showCartUpdateError() {
    const drawer = this.closest('cart-drawer-component');
    const host = drawer?.querySelector('.cart-drawer__inner') || this;
    let toast = host.querySelector('.gullyo-cart-update-error');

    if (!(toast instanceof HTMLElement)) {
      toast = document.createElement('div');
      toast.className = 'gullyo-cart-update-error';
      toast.setAttribute('role', 'status');
      toast.textContent = "We couldn't update your bag. Please try again.";
      host.appendChild(toast);
    }

    toast.hidden = false;
    toast.classList.add('gullyo-cart-update-error--visible');

    if (this.#errorToastTimer) {
      clearTimeout(this.#errorToastTimer);
    }

    this.#errorToastTimer = window.setTimeout(() => {
      toast.classList.remove('gullyo-cart-update-error--visible');
      toast.hidden = true;
    }, 4000);
  }

  /**
   * @param {number} line
   * @param {boolean} busy
   * @param {'updating' | 'removing'} [mode]
   */
  #setLineBusy(line, busy, mode = 'updating') {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return;

    if (busy) {
      row.toggleAttribute('aria-busy', true);
      if (mode === 'updating') {
        row.classList.add('gullyo-cart-item--updating');
      }
      row.querySelectorAll('button, input').forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          control.disabled = true;
        }
      });
      return;
    }

    if (!row.classList.contains('gullyo-cart-item--removing')) {
      row.removeAttribute('aria-busy');
      row.classList.remove('gullyo-cart-item--updating');
      row.querySelectorAll('button, input').forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          control.disabled = false;
        }
      });
    }
  }

  /**
   * Releases disabled state preserved by a section morph after the request has settled.
   * @param {number} line
   */
  releaseTransientControls(line) {
    const rows = this.querySelectorAll('.gullyo-cart-item:not(.gullyo-cart-item--removing)');
    const row = rows[line - 1];
    if (!(row instanceof HTMLElement)) return;

    row.removeAttribute('aria-busy');
    row.classList.remove('gullyo-cart-item--updating');

    const quantityInput = row.querySelector('input[type="number"]');
    if (quantityInput instanceof HTMLInputElement && !row.hasAttribute('data-gullyo-line-unavailable')) {
      quantityInput.disabled = false;
    }

    const removeButton = row.querySelector('.cart-items__remove');
    if (removeButton instanceof HTMLButtonElement && !removeButton.hidden) {
      removeButton.disabled = false;
    }

    const selector = row.querySelector('cart-quantity-selector-component');
    if (selector && typeof /** @type {any} */ (selector).updateButtonStates === 'function') {
      /** @type {any} */ (selector).updateButtonStates();
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getScrollContainer() {
    return (
      this.closest('.cart-drawer__items') ||
      this.querySelector('.cart-drawer__items') ||
      this.closest('scroll-hint.cart-drawer__items')
    );
  }

  #syncDrawerEmptyState() {
    if (!this.isDrawer) return;

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const isEmpty = !this.querySelector('.cart-form') && !this.querySelector('.gullyo-cart-item');
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  /**
   * @param {boolean} wasOpen
   */
  #restoreDrawerOpenState(wasOpen) {
    if (!wasOpen || !this.isDrawer) return;

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    if (!(drawer instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement)) return;
    if (dialog.open) return;

    if (typeof /** @type {any} */ (drawer).showDialog === 'function') {
      /** @type {any} */ (drawer).showDialog();
    } else {
      dialog.showModal();
    }
  }

  #applyPostUpdateFocus() {
    if (this.#pendingFocusEmptyCart) {
      this.#pendingFocusEmptyCart = false;
      const continueShopping =
        this.querySelector('.cart-items__empty-button') || document.getElementById('cart-drawer-heading-empty');
      continueShopping?.focus({ preventScroll: true });
      return;
    }

    if (!this.#pendingFocusLine) return;

    const removedLine = this.#pendingFocusLine;
    this.#pendingFocusLine = null;
    const rows = this.refs.cartItemRows ?? [];
    const targetRow = rows[removedLine - 1] || rows[removedLine - 2] || rows[0];
    const focusTarget =
      targetRow?.querySelector('.gullyo-cart-item__title') ||
      targetRow?.querySelector('.quantity-minus, .gullyo-cart-item__remove, .quantity-plus');
    focusTarget?.focus({ preventScroll: true });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if (selector.matches('cart-quantity-selector-component')) {
          input.value = item.quantity.toString();
          const row = selector.closest('.gullyo-cart-item');
          if (row instanceof HTMLElement) {
            row.dataset.quantity = item.quantity.toString();
          }
        }

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
